import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import axios from '../../utils/axiosConfig';
import GalleryForm from '../../components/GalleryForm';

const ManageGallery = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, image: null });

  const fetchImages = async () => {
    try {
      const response = await axios.get('/api/v2/gallery');
      setImages(response.data);
      setError('');
    } catch (err) {
      console.error('Error fetching images:', err);
      setError('Failed to load gallery images');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImages();
  }, []);

  const handleFeatureToggle = async (image) => {
    try {
      await axios.put(`/api/v2/gallery/${image._id}`, {
        ...image,
        featured: !image.featured
      });
      fetchImages();
    } catch (err) {
      console.error('Error updating image:', err);
      setError('Failed to update image');
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.image) return;

    try {
      await axios.delete(`/api/v2/gallery/${deleteDialog.image._id}`);
      setDeleteDialog({ open: false, image: null });
      fetchImages();
    } catch (err) {
      console.error('Error deleting image:', err);
      setError('Failed to delete image');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Manage Gallery
      </Typography>

      <GalleryForm onSuccess={fetchImages} />

      {error && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mt: 3 }}>
        {images.map((image) => (
          <Grid item xs={12} sm={6} md={4} key={image._id}>
            <Card>
              <CardMedia
                component="img"
                height="200"
                image={image.image}
                alt={image.title}
                sx={{ objectFit: 'cover' }}
              />
              <CardContent>
                <Typography variant="h6" noWrap>
                  {image.title}
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={image.featured}
                      onChange={() => handleFeatureToggle(image)}
                    />
                  }
                  label="Featured"
                />
              </CardContent>
              <CardActions>
                <IconButton
                  onClick={() => setDeleteDialog({ open: true, image })}
                  color="error"
                >
                  <Delete />
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, image: null })}
      >
        <DialogTitle>Delete Image</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteDialog.image?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false, image: null })}
          >
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageGallery;
